import { createId } from '@paralleldrive/cuid2'
import { foldEvents, projectRow, repairExists } from 'api'
import type { EventType, FotoIntervento, InterventoStatus } from 'api'
import Bluebird from 'bluebird'

import { hardDelete } from '@/lib/sync-manager/soft-delete-addon'
import type { DexieDatabase, InterventoDoc } from '@/db/local/dexie-db'
import type { InterventoFormValues } from './components/intervento-form/useInterventoForm'
import { appendEvent, eventsOf } from './eventi'

/**
 * The intervento as this save leaves it, with the events it records: created
 * (when new), started and completed once each, with the ids the server uses for them, so a
 * device that rebuilds them from the row makes the same ones. Completing again
 * records a correction when the esito changed (only while never repaired), never
 * a second completion.
 * Status, esito and current operatore follow from the events.
 */
function withEvents(
  doc: InterventoDoc,
  status: InterventoStatus,
  userId: string,
  isNew: boolean
): InterventoDoc {
  const esito = doc.esito
  // Before the events below move it
  const wasCompleted = doc.status === 'completato'
  const has = (tipo: EventType) =>
    eventsOf(doc).some((event) => event.tipo === tipo)
  const record = (tipo: EventType, fields = {}) => {
    doc = appendEvent(doc, {
      id: `${doc.id}:${tipo}`,
      tipo,
      operatore_id: userId,
      ...fields,
    })
  }

  if (isNew) record('creato')
  if (status !== 'bozza' && !has('avviato')) record('avviato')
  if (status === 'completato') {
    const [inspection] = foldEvents(eventsOf(doc)).phases
    if (!has('completato')) {
      // A completion records its esito: the form asks for it, and a sync that
      // resets the form between the question and the save must not get one
      // through without (one did, on 2026-10-29). A row completed already,
      // without events to say so (no operatore to record them as), only saves
      if (!esito && !wasCompleted) {
        throw new Error('Esito mancante: impossibile completare')
      }
      record('completato', { esito })
    } else if (
      esito &&
      inspection?.closingId &&
      inspection.esito !== esito &&
      // Once a repair exists, the row's esito is the repairs' business: the
      // inspection's own isn't what the form shows, and the server refuses the
      // inspector's correction. A repair cancelled before closing doesn't count
      !repairExists(foldEvents(eventsOf(doc)))
    ) {
      // A correction of the inspection names no repair
      doc = appendEvent(doc, {
        tipo: 'correzione',
        operatore_id: userId,
        riparazione_id: null,
        esito,
      })
    }
  }

  const events = eventsOf(doc)
  return {
    ...doc,
    eventi: events,
    ...projectRow(events, { status: doc.status, esito }),
  }
}

/**
 * Writes the intervento form to the local database: the intervento, its photos
 * and the photos removed in the form. Syncing is left to the caller.
 */
export async function saveIntervento(
  db: DexieDatabase,
  formData: InterventoFormValues,
  status: InterventoStatus,
  userId: string
) {
  const { intervento, fotoIntervento, fotoInterventoToDeleteIds } = formData

  // Update existing intervento
  const interventoId = intervento!.id as string
  const preparedRecord = {
    ...intervento,
    data: intervento?.data ?? new Date(),
    id: interventoId ?? createId(),
    // Only set operatore_id if it's not already set (i.e., for new records)
    operatore_id: intervento?.operatore_id ?? userId,
    status: intervento?.status ?? 'bozza',
  }

  const updatedIntervento = await db.transaction(
    'rw',
    db.interventi,
    db.foto_interventi,
    async () => {
      // What the form doesn't carry (the events, the current operatore) stays.
      // The events of a row pulled before the app knew them come from the row
      // as stored, not from the form's edits
      const stored = await db.interventi.get(preparedRecord.id)
      const updatedInterventoId = await db.interventi.put(
        withEvents(
          {
            ...stored,
            ...preparedRecord,
            eventi: stored ? eventsOf(stored) : [],
          } as InterventoDoc,
          status,
          userId,
          !stored
        )
      )
      const interventoRecord = await db.interventi.get(updatedInterventoId)

      await Promise.all(
        Object.entries(fotoIntervento || {}).map(async ([_, foto]) => {
          if (!foto) return

          const preparedFoto = {
            ...foto,
            id: foto.id ?? createId(),
            // Only set operatore_id if it's not already set (i.e., for new records)
            operatore_id: foto.operatore_id ?? userId,
            // Always the intervento's, also after its committente changes (K25)
            committente_id: intervento?.committente_id ?? null,
            intervento_id: updatedInterventoId,
          }
          await db.foto_interventi.put(preparedFoto as FotoIntervento)
        })
      )

      await Bluebird.map(fotoInterventoToDeleteIds ?? [], async (id) => {
        return db.foto_interventi.update(id, { deleted_at: new Date() })
      })

      return interventoRecord
    }
  )

  return updatedIntervento
}

/**
 * A new intervento, saved as a draft the moment it is opened, so its ID (which
 * the operatore may already have written on the drain) survives a reload or a
 * closed app. It stays on this device until its first save (the interventi
 * push holds it back): the server requires the tipologia and the other fields
 * the draft form asks for.
 */
export async function createDraft(
  db: DexieDatabase,
  { idInterno, userId }: { idInterno: string; userId: string }
): Promise<InterventoDoc> {
  const id = createId()
  const now = new Date()
  // No tipologia until the first save: the draft schema asks for it
  const draft = withEvents(
    {
      id,
      id_interno: idInterno,
      status: 'bozza',
      caditoie_num: 1,
      coordinate: null,
      indirizzo: null,
      note_pre: null,
      note_post: null,
      operatore_id: userId,
      data: now,
      committente_id: null,
      esito: null,
      current_operatore_id: null,
      created_at: now,
      updated_at: now,
      deleted_at: null,
      eventi: [],
    } as unknown as InterventoDoc,
    'bozza',
    userId,
    true
  )
  // Unsent like any new row; the interventi push holds it back until saved
  await db.interventi.put(draft)
  return draft
}

/** Whether the intervento exists only on this device: a draft never saved. */
export const neverSaved = (doc: Pick<InterventoDoc, 'tipologia_caditoia'>) =>
  !doc.tipologia_caditoia

/**
 * Deletes a draft: one never saved is removed from the device (the server never
 * had it), a saved one is soft-deleted and the deletion pushed.
 */
export async function deleteDraft(
  db: DexieDatabase,
  interventoId: string
): Promise<void> {
  const stored = await db.interventi.get(interventoId)
  if (!stored || stored.status !== 'bozza') {
    throw new Error('Si possono eliminare solo le bozze')
  }
  if (neverSaved(stored)) {
    await hardDelete(db.interventi, interventoId)
    return
  }
  await db.interventi.update(interventoId, { deleted_at: new Date() })
}
